import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'

function assertSuccess(body: unknown) {
  const b = body as { success?: boolean; message?: string; data?: unknown }
  if (!b?.success) {
    throw new Error(sanitizeBusinessMessage(b?.message, 'Request failed'))
  }
  return b.data
}

function apiError(err: unknown, fallback: string) {
  return toUserFacingApiError(err, fallback)
}

export type DiscountCodeVisibilityStatus =
  | 'active_now'
  | 'scheduled'
  | 'expired'
  | 'disabled'
  | 'exhausted'

export type DiscountCodeRecord = {
  id: number
  storeId: number
  code: string
  discountType: 'percent' | 'fixed'
  discountValue: number
  minOrderSubtotal: number | null
  maxDiscountAmount: number | null
  startsAt: string | null
  endsAt: string | null
  usageLimit: number | null
  timesUsed: number
  isActive: boolean
  visibilityStatus: DiscountCodeVisibilityStatus
  createdAt: string
  updatedAt: string
}

export async function listDiscountCodes(storeId: number): Promise<{ items: DiscountCodeRecord[] }> {
  try {
    const { data } = await api.get(
      `/storebusiness/stores/${encodeURIComponent(String(storeId))}/discount-codes`,
    )
    return assertSuccess(data) as { items: DiscountCodeRecord[] }
  } catch (e) {
    throw apiError(e, 'Failed to load discount codes')
  }
}

export async function createDiscountCode(
  storeId: number,
  payload: {
    code: string
    discountType: 'percent' | 'fixed'
    discountValue: number
    minOrderSubtotal?: number | null
    maxDiscountAmount?: number | null
    startsAt?: string | null
    endsAt?: string | null
    usageLimit?: number | null
    isActive?: boolean
  },
): Promise<DiscountCodeRecord> {
  try {
    const { data } = await api.post(
      `/storebusiness/stores/${encodeURIComponent(String(storeId))}/discount-codes`,
      payload,
    )
    return assertSuccess(data) as DiscountCodeRecord
  } catch (e) {
    throw apiError(e, 'Failed to create discount code')
  }
}

export async function updateDiscountCode(
  storeId: number,
  codeId: number,
  payload: Partial<{
    code: string
    discountType: 'percent' | 'fixed'
    discountValue: number
    minOrderSubtotal: number | null
    maxDiscountAmount: number | null
    startsAt: string | null
    endsAt: string | null
    usageLimit: number | null
    isActive: boolean
  }>,
): Promise<DiscountCodeRecord> {
  try {
    const { data } = await api.patch(
      `/storebusiness/stores/${encodeURIComponent(String(storeId))}/discount-codes/${encodeURIComponent(String(codeId))}`,
      payload,
    )
    return assertSuccess(data) as DiscountCodeRecord
  } catch (e) {
    throw apiError(e, 'Failed to update discount code')
  }
}

export async function deleteDiscountCode(storeId: number, codeId: number): Promise<void> {
  try {
    const { data } = await api.delete(
      `/storebusiness/stores/${encodeURIComponent(String(storeId))}/discount-codes/${encodeURIComponent(String(codeId))}`,
    )
    assertSuccess(data)
  } catch (e) {
    throw apiError(e, 'Failed to delete discount code')
  }
}
